import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const schema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(['product', 'service', 'mixed']),
  sort_order: z.coerce.number().int().min(0).max(9999).optional(),
});

export type CategoryFormValues = z.infer<typeof schema>;

interface CategoryFormDialogProps {
  title: string;
  initial_values?: Partial<CategoryFormValues>;
  submitting: boolean;
  onSubmit: (values: CategoryFormValues) => Promise<void>;
  onCancel: () => void;
}

export function CategoryFormDialog({
  title,
  initial_values,
  submitting,
  onSubmit,
  onCancel,
}: CategoryFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial_values?.name ?? '',
      kind: initial_values?.kind ?? 'mixed',
      sort_order: initial_values?.sort_order ?? 0,
    },
  });

  const onSubmitForm = async (values: CategoryFormValues): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <form onSubmit={form.handleSubmit(onSubmitForm)} noValidate>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('catalog.categories.name')}</Label>
              <Input id="name" autoFocus maxLength={120} {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="kind">{t('catalog.categories.kind')}</Label>
              <select
                id="kind"
                {...form.register('kind')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="product">{t('catalog.categories.kind_product')}</option>
                <option value="service">{t('catalog.categories.kind_service')}</option>
                <option value="mixed">{t('catalog.categories.kind_mixed')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_order">{t('catalog.categories.sort_order')}</Label>
              <Input
                id="sort_order"
                type="number"
                min={0}
                max={9999}
                {...form.register('sort_order')}
              />
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
